import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, Inject } from 'ng-metadata/core';
import * as angular from 'angular';

import { MediaDocument } from './media-document.model';
import './media-document.component.scss';

/**
 * @ngdoc directive
 * @name mwMediaDocument
 * @module mw.components.contentDocuments
 *
 * @param {object} [document] - The content document. See
 * [Content Document Model](https://github.com/meltwater/gaf-ng-components/blob/master/src/lib/components/content-documents/media-document/media-document.model.ts)
 *
 * @param {string} [active-tray-content] - the id of a tray item that should be active by default, supported tray id's include 'duplicates', 'socialEcho' and 'socialEngagement'
 * @param {string} [display-mode] - what mode the documents are displayed in. we currently support 'standard' (default), 'mini', 'indy' and 'compact'.  NOTE: 'compact' is deprecated
 * @param {string} [media-emphasis] - level of emphasis for the media, will determine position and size of the media 'high', 'medium' (default) & 'low'
 *
 * @param {boolean} [is-selectable=true] - Control if the document is selectable (if true, shows a checkbox).
 * @param {boolean} [select-mode=checkbox] - Control how document is selectable, supported options include 'checkbox' and 'full-document'.
 * @param {boolean} [is-avatar-visible=true] - determines whether or not the avatar will be shown
 * @param {boolean} [is-avatar-padding-on=true] - this will add padding to the document-card content if is-avatar-visible is true as well
 * @param {boolean} [is-title-visible=true] - determines whether or not the title will be shown
 * @param {boolean} [is-ingress-visible=true] - determines whether or not the ingress actually shows
 * @param {boolean} [is-match-sentence-visible=true] - determines whether or not the match sentence will be shown
 * @param {boolean} [is-keywords-visible=true] - determines whether or not the keywords component will be shown
 * @param {boolean} [is-keywords-clickable=true] - determines whether or not the keywords component is clickable
 * @param {boolean} [is-media-visible=true] - determines whether or not to show the media of the document
 * @param {boolean} [is-media-placeholder-visible=true] - determines whether or not to show the media placeholder
 * @param {boolean} [is-tags-visible=true] - determines whether or not tags are visible
 * @param {boolean} [is-reach-visible=true] - determines whether or not reach is visible
 * @param {boolean} [is-social-echo-visible=true] - determines whether or not social echo is visible
 * @param {boolean} [is-social-engagement-visible=true] - determines whether or not social engagement is visible
 * @param {boolean} [is-duplicates-visible=true] - determines whether or not duplicates are available
 * @param {boolean} [is-sentiment-visible=true] - determines whether or not sentiment is visible
 * @param {boolean} [is-inline-sentiment=false] - EXPERIMENTAL: Enable new sentiment selector (only in desktop)
 * @param {boolean} [is-icm-enabled=false] - determines whether or not authors are linked to the ICM profile pages
 * @param {number} [duplicates-page-size=10] - the page size of duplicates docs when they are shown
 * @param {string} [locale] - The locale/language that the custom date range picker will use
 * @param {string} [timezone] - The timezone to be used by the custom date range picker. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 *
 * @param {expression} [on-document-click] - Angular expression to handle when the document is clicked. $event is the MediaDocument model.
 * @param {expression} [on-select-toggle] - Angular expression to evaluate when the selected state of the document is toggled. $event is a boolean indicating selected(true)/unselected(false) state.
 * @param {expression} [on-title-click] - Angular expression to handle when the document title is clicked. $event is the MediaDocument model.
 * @param {expression} [on-timestamp-click] - Angular expression to handle when the document timestamp is clicked. $event is the MediaDocument model.
 * @param {expression} [on-avatar-click] - Angular expression to handle when the document avatar is clicked. $event is the MediaDocument model.
 * @param {expression} [on-sentiment-update] - Angular expression to handle when sentiment is updated. $event is an object containing a MediaDocument and Sentiment ({document, sentiment}).
 * @param {expression} [on-sentiment-reset] - Angular expression to handle when sentiment is reset. $event is the MediaDocument model.
 * @param {expression} [on-tag-removal] - Angular expression to handle when a tag is removed. $event is an object containing a MediaDocument and Tag ({document, tag}).
 * @param {expression} [on-tag-click] - Angular expression to handle when a tag is clicked. $event is a tag.
 * @param {expression} [on-keyword-click] - Angular expression to handle when a keyword is clicked. $event is a keyword.
 * @param {expression} [on-engagement-toggle] - Angular expression to handle when a user clicks on engagement action button. $event is a flag where the social engagement is active or not.
 * @param {expression} [on-duplicates-toggle] - Angular expression to handle when a user clicks on duplicate docs link. $event is a boolean which tells if duplicate is active or not.
 * @param {expression} [on-social-echo-toggle] - Angular expression to handle when social echo is toggled. $event is a boolean which tells if social echo is active or not.
 *
 * @description
 * `<mw-media-document>` is a component used to display a single media content document.
 *
 *
 * @usage
 * <hljs hljs-language="html">
 *     <mw-media-document
 *       document="$ctrl.contentDocument"
 *      is-selectable="$ctrl.displaySettings.isSelectable"
 *      is-avatar-visible="$ctrl.displaySettings.isAvatarVisible"
 *      is-avatar-padding-on="$ctrl.displaySettings.isAvatarPaddingOn"
 *      is-title-visible="$ctrl.displaySettings.isTitleVisible"
 *      is-ingress-visible="$ctrl.displaySettings.isOpeningTextVisible"
 *      is-match-sentence-visible="$ctrl.displaySettings.isHitSentenceVisible"
 *      is-media-visible="$ctrl.displaySettings.isMediaVisible"
 *      is-media-plaeholder-visible="$ctrl.displaySettings.isMediaPlcaeholderVisible"
 *      is-keywords-visible="$ctrl.displaySettings.isKeywordsVisible"
 *      is-tags-visible="$ctrl.displaySettings.isTagsVisible"
 *      is-inline-sentiment-enabled="$ctrl.displaySettings.inlineSentiment"
 *      is-social-echo-visible="$ctrl.displaySettings.isSocialEchoVisible"
 *      is-social-engagement-visible="$ctrl.displaySettings.isSocialEngagementVisible"
 *      display-mode="$ctrl.displaySettings.isCompact ? 'compact' : 'standard'"
 *      on-select-toggle="$event.document.selected = !$event.document.selected"
 *      on-sentiment-update="$ctrl.handleSentimentUpdate($event.document, $event.sentiment)"
 *      on-sentiment-reset="$ctrl.handleSentimentReset($event.document)"
 *      on-tag-removal="$ctrl.handleTagRemoval($event.document, $event.tag)"
 *      on-title-click="$ctrl.handleHostedContentClick($event)"
 *      on-timestamp-click="$ctrl.handleHostedContentClick($event)"
 *      on-avatar-click="$ctrl.handleHostedContentClick($event)"
 *      timezone="$ctrl.demosTimezoneService.timezone"
 *      active-tray-content="$ctrl.activeTrayContent">
 *      <mw-content-document-actions>
 *          <md-button ng-href="{{contentDocument.url}}" target="_blank" class="md-icon-button" aria-label="{'Open' | translate}" >
 *              <md-tooltip md-delay="500"><span translate>Open</span></md-tooltip>
 *              <md-icon md-svg-icon="action:ic_open_in_new_24px"></md-icon>
 *          </md-button>
 *          <md-menu md-position-mode="target-right target" md-offset="0 52"></md-menu>
 *      </mw-content-document-actions>
 *  </mw-media-document>
 * </hljs>
 */

const mediaDocumentTemplate = `
<div class="mw-content-document mw-media-document--{{$ctrl.displayMode}}" ng-class="{ 'mw-content-document--avatar-is-visible': $ctrl.isAvatarVisible && $ctrl.isAvatarPaddingOn, 'mw-content-document--is-selectable': $ctrl.isSelectable, 'mw-content-document--is-clickable': $ctrl.isDocumentClickable, 'mw-content-document--is-selected': $ctrl.isSelected($ctrl.document)}" ng-click="$ctrl.handleDocumentClick($ctrl.document, $event)">
    <div class="mw-content-document__content-wrapper">
        <div class="mw-content-document__unread-indicator" ng-if="$ctrl.document.unread">&nbsp;</div>
        <ng-transclude ng-transclude-slot="mwContentDocumentMedia" ng-if="$ctrl.mediaEmphasis === 'high' && $ctrl.isMediaVisible" >
            <mw-content-document-media>
                <mw-content-document-image
                    ng-if="::$ctrl.document.mediaType !== 'youtube'"
                    image-url="::$ctrl.document.mediaImageUrl"
                    link-url="::!$ctrl.isDocumentClickable ? $ctrl.document.url : ''"
                    display-mode="'standard'"
                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                </mw-content-document-image>
                <mw-content-document-youtube-video
                    ng-if="::$ctrl.document.mediaType === 'youtube'"
                    enable-playback="::$ctrl.enableYoutubePlayback"
                    on-click="$ctrl.enableYoutubePlayback = true"
                    video-url="::!$ctrl.isDocumentClickable && $ctrl.document.videoUrl"
                    display-mode="'standard'"
                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                </mw-content-document-youtube-video>
            </mw-content-document-media>
        </ng-transclude>
        <div class="mw-content-document__main-container" layout="row">
            <div class="mw-content-document__checkbox-container" ng-if="::$ctrl.isSelectable">
                <md-checkbox
                    aria-label="Select Document"
                    ng-checked="$ctrl.isSelected($ctrl.document)"
                    ng-click="$ctrl.handleSelectToggle($ctrl.document)"
                    class="md-primary">
                </md-checkbox>
            </div>

            <div class="mw-content-document__column-two" flex="100">
                <ng-transclude ng-transclude-slot="mwContentDocumentHeader"></ng-transclude>
                <mw-content-document-header ng-if="!$ctrl.$transclude.isSlotFilled('mwContentDocumentHeader')">
                    <mw-content-document-source-avatar ng-if="$ctrl.isAvatarVisible"
                        image-url="::$ctrl.document.sourceAvatarImgUrl"
                        link-url="::!$ctrl.isDocumentClickable ? $ctrl.document.sourceAvatarLinkUrl : ''"
                        source-name="::$ctrl.document.sourceName"
                        document-title="::$ctrl.document.title || $ctrl.document.body"
                        is-rounded="::$ctrl.document.sourceAvatarRounded"
                        on-click="$ctrl.handleAvatarClick($ctrl.document, $event)"
                        media-type="::$ctrl.document.mediaType"
                        media-type="::$ctrl.document.mediaTypeIcon"
                        >
                    </mw-content-document-source-avatar>
                    <mw-content-document-source-name
                        text="::$ctrl.document.sourceName"
                        url="::!$ctrl.isDocumentClickable ? $ctrl.document.sourceNameUrl : ''">
                    </mw-content-document-source-name>
                    <mw-content-document-authors
                        ng-class="::{'mw-content-document-authors--needs-separator': $ctrl.document.sourceNameUrl !== $ctrl.document.authors[0].url}"
                        ng-if="::$ctrl.document.authors.length !== 0 && $ctrl.document.authors">
                        <mw-content-document-author
                            ng-repeat="author in ::$ctrl.document.authors track by $index"
                            ng-if="::author.name"
                            text="::author.name"
                            url="::$ctrl.isIcmEnabled && author.icmUrl ? (!$ctrl.isDocumentClickable ? author.icmUrl : '') : (!$ctrl.isDocumentClickable ? author.url : '')">
                        </mw-content-document-author>
                    </mw-content-document-authors>
                    <mw-content-document-source-country
                        country-code="::$ctrl.document.sourceCountryCode">
                    </mw-content-document-source-country>
                    <mw-content-document-timestamp
                        date="::$ctrl.document.date"
                        link-url="::!$ctrl.isDocumentClickable ? $ctrl.document.url : ''"
                        timezone="$ctrl.timezone"
                        on-click="$ctrl.handleTimestampClick($ctrl.document, $event);">
                    </mw-content-document-timestamp>
                    <mw-content-document-actions>
                        <ng-transclude ng-transclude-slot="mwContentDocumentActions"></ng-transclude>
                    </mw-content-document-actions>
                </mw-content-document-header>

                <div class="mw-content-document__content" layout="row" dir="{{::$ctrl.contentDirection}}" flex="100">
                    <div flex="100">
                        <ng-transclude ng-transclude-slot="mwContentDocumentTitle">
                            <mw-content-document-title
                                ng-if="$ctrl.isTitleVisible"
                                text="::$ctrl.document.title"
                                document-url="::!$ctrl.isDocumentClickable ? $ctrl.document.titleUrl : ''"
                                on-click="$ctrl.handleTitleClick($ctrl.document, $event);">
                            </mw-content-document-title>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentIngress">
                            <mw-content-document-ingress
                                ng-if="$ctrl.isIngressVisible"
                                text="::$ctrl.document.ingress">
                            </mw-content-document-ingress>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentMatchSentence">
                            <mw-content-document-match-sentence
                                ng-if="$ctrl.isMatchSentenceVisible"
                                text="::$ctrl.document.matchSentences">
                            </mw-content-document-match-sentence>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentBody">
                            <mw-content-document-body
                                ng-if="::$ctrl.displaySettings.isMatchSentenceVisible || $ctrl.document.body"
                                text="::[$ctrl.document.body]"
                                truncation-length="::$ctrl.document.bodyTruncationLength">
                            </mw-content-document-body>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentMedia" ng-if="$ctrl.mediaEmphasis === 'medium' && $ctrl.isMediaVisible" >
                            <mw-content-document-media>
                                <mw-content-document-image
                                    ng-if="::$ctrl.document.mediaType !== 'youtube'"
                                    image-url="::$ctrl.document.mediaImageUrl"
                                    link-url="::!$ctrl.isDocumentClickable ? $ctrl.document.url : ''"
                                    display-mode="'standard'"
                                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                                </mw-content-document-image>
                                <mw-content-document-youtube-video
                                    ng-if="::$ctrl.document.mediaType === 'youtube'"
                                    enable-playback="::$ctrl.enableYoutubePlayback"
                                    on-click="$ctrl.enableYoutubePlayback = true"
                                    video-url="::$ctrl.document.videoUrl"
                                    display-mode="'standard'"
                                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                                </mw-content-document-youtube-video>
                            </mw-content-document-media>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentKeywords">
                            <mw-content-document-keywords
                                ng-if="$ctrl.isKeywordsVisible"
                                keywords="::$ctrl.document.keywords"
                                is-keywords-clickable="$ctrl.isKeywordsClickable"
                                on-keyword-click="$ctrl.handleKeywordClick($event)"
                                truncate-limit="5"
                                truncate-is-collapsible="$ctrl.keywordsTruncateMoreOnly">
                            </mw-content-document-keywords>
                        </ng-transclude>
                        <ng-transclude ng-transclude-slot="mwContentDocumentTags">
                            <mw-content-document-tags
                                ng-if="$ctrl.isTagsVisible"
                                tags="$ctrl.document.tags"
                                on-tag-click="$ctrl.handleTagClick($event)"
                                on-remove-tag="$ctrl.handleTagRemoval($ctrl.document, $event)">
                            </mw-content-document-tags>
                        </ng-transclude>
                    </div>
                    <div ng-if="(ctrl.displayMode === 'compact' || $ctrl.mediaEmphasis === 'low') && $ctrl.isMediaVisible" flex="noshrink">
                        <ng-transclude ng-transclude-slot="mwContentDocumentMedia" ng-if="$ctrl.displayMode === 'compact' || $ctrl.mediaEmphasis === 'low'">
                            <mw-content-document-media>
                                <mw-content-document-image
                                    ng-if="::$ctrl.document.mediaType !== 'youtube'"
                                    image-url="::$ctrl.document.mediaImageUrl"
                                    link-url="::!$ctrl.isDocumentClickable ? $ctrl.document.url : ''"
                                    display-mode="'compact'"
                                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                                </mw-content-document-image>
                                <mw-content-document-youtube-video
                                    ng-if="::$ctrl.document.mediaType === 'youtube'"
                                    enable-playback="::$ctrl.enableYoutubePlayback"
                                    on-click="$ctrl.enableYoutubePlayback = true"
                                    video-url="::$ctrl.document.videoUrl"
                                    display-mode="!$ctrl.enableYoutubePlayback ? 'compact' : 'standard'"
                                    is-placeholder-visible="::$ctrl.isMediaPlaceholderVisible">
                                </mw-content-document-youtube-video>
                            </mw-content-document-media>
                        </ng-transclude>
                    </div>
                </div>
                <ng-transclude class="mw-content-document__footer-slot" ng-transclude-slot="mwContentDocumentFooter">
                    <mw-content-document-footer>

                        <mw-content-document-action-trigger
                            ng-if="::$ctrl.isDuplicatesVisible && $ctrl.document.dupDocs && $ctrl.document.dupDocs.length > 0"
                            text="'mw-content-document__action-trigger--duplicates' | translate:{count: $ctrl.document.dupDocs.length}:'messageformat'"
                            on-click="$ctrl.handleDuplicatesToggle($ctrl.document)"
                            is-active="$ctrl.isDuplicatesActive">
                        </mw-content-document-action-trigger>

                        <mw-content-document-action-trigger
                            ng-if="::$ctrl.isReachVisible && $ctrl.document.reachCount"
                            link="::!$ctrl.isDocumentClickable ? $ctrl.document.reachUrl : ''"
                            on-click="$ctrl.handleReachToggle()"
                            text="::(($ctrl.document.reachCount | mwTruncateNumber) + ' ' + ('mw-content-document__action-trigger--reach' | translate))">
                            is-active="::$ctrl.isReachActive"
                        </mw-content-document-action-trigger>

                        <mw-content-document-action-trigger
                            ng-if="::$ctrl.document.socialScores && $ctrl.isSocialEchoVisible"
                            text="::('socialecho__label' | translate)"
                            on-click="$ctrl.handleSocialEchoToggle()"
                            is-active="$ctrl.isSocialEchoActive">
                        </mw-content-document-action-trigger>

                        <mw-content-document-action-trigger
                            ng-if="::$ctrl.document.engagement && $ctrl.isSocialEngagementVisible"
                            text="::(($ctrl.document.engagement | mwTruncateNumber) + ' ' + ('mw-content-document__action-trigger--engagement' | translate))"
                            on-click="$ctrl.handleSocialEngagementToggle()"
                            is-active="$ctrl.isSocialEngagementActive">
                        </mw-content-document-action-trigger>

                        <mw-content-document-sentiment
                            ng-if="::$ctrl.document.sentiment && $ctrl.isSentimentVisible"
                            on-sentiment-update="$ctrl.handleSentimentUpdate($ctrl.document, $event)"
                            on-sentiment-reset="$ctrl.handleSentimentReset($ctrl.document, $event)"
                            sentiment-override="$ctrl.document.sentimentOverride"
                            sentiment="$ctrl.document.sentiment"
                            is-inline-sentiment-enabled="$ctrl.isInlineSentimentEnabled">
                        </mw-content-document-sentiment>

                    </mw-content-document-footer>
                </ng-transclude>
            </div>
        </div>
        <div class="mw-content-document__content-overlay"></div>
    <div>
    <mw-content-document-tray>
        <ng-transclude ng-transclude-slot="mwContentDocumentTrayContent"></ng-transclude>

        <mw-content-document-social-echo
            class="mw-content-document-tray__content--auto-pad"
            ng-if="$ctrl.isSocialEchoActive"
            twitter-shares="::$ctrl.document.socialScores.tw_shares"
            facebook-shares="::$ctrl.document.socialScores.fb_shares"
            >
        </mw-content-document-social-echo>

        <mw-content-document-social-engagement
            class="mw-content-document-tray__content--auto-pad"
            ng-if="$ctrl.isSocialEngagementActive"
            replies="::$ctrl.document.socialScores.tw_replies"
            retweets="::$ctrl.document.socialScores.tw_retweets"
            likes="::$ctrl.document.socialScores.tw_likes"
            last-updated-at="::$ctrl.document.engagementLastUpdateDate">
        </mw-content-document-social-engagement>

        <mw-content-document-duplicate-docs
            ng-class="{'mw-content-document-duplicate-docs--selectable': $ctrl.isSelectable}"
            ng-if="$ctrl.isDuplicatesVisible && $ctrl.isDuplicatesActive">
            <mw-media-document
                ng-repeat="dupContentDocument in $ctrl.document.dupDocs | limitTo:$ctrl.document.dupDocsVisibleCount"
                document="dupContentDocument"
                display-mode="::$ctrl.isCompact ? 'compact' : 'standard'"
                is-selectable="::$ctrl.isSelectable"
                is-selected="$ctrl.isSelected"
                is-avatar-visible="$ctrl.isAvatarVisible"
                is-ingress-visible="$ctrl.isIngressVisible"
                is-media-visible="$ctrl.isMediaVisible"
                is-match-sentence-visible="$ctrl.isMatchSentenceVisible"
                is-tags-visible="$ctrl.isTagsVisible"
                is-keywords-visible="$ctrl.isKeywordsVisible"
                is-keywords-clickable="$ctrl.isKeywordsClickable"
                is-social-echo-visible="$ctrl.isSocialEchoVisible"
                is-social-engagement-visible="$ctrl.isSocialEngagementVisible"
                on-keyword-click="$ctrl.handleKeywordClick($event.keyword)"
                on-select-toggle="$ctrl.handleSelectToggle(dupContentDocument, $ctrl.document)"
                on-sentiment-update="$ctrl.handleSentimentUpdate(dupContentDocument, $event.sentiment)"
                on-sentiment-reset="$ctrl.handleSentimentReset(dupContentDocument)"
                is-inline-sentiment-enabled="$ctrl.isInlineSentimentEnabled"
                is-icm-enabled="::$ctrl.isIcmEnabled"
                on-tag-removal="$ctrl.handleTagRemoval(dupContentDocument, $event.tag)"
                on-tag-click="$ctrl.handleTagClick($event.tag)"
                on-timestamp-click="$ctrl.handleTimestampClick(dupContentDocument)"
                on-avatar-click="$ctrl.handleAvatarClick(dupContentDocument)"
                on-title-click="$ctrl.handleTitleClick(dupContentDocument)"
                >
                <mw-content-document-actions>
                    <ng-transclude ng-transclude-slot="mwContentDocumentDuplicateDocsActions"></ng-transclude>
                </mw-content-document-actions>
            </mw-media-document>
            <mw-content-document-tray-footer ng-if="$ctrl.document.dupDocsVisibleCount > 0" on-close="$ctrl.handleDuplicatesToggle($ctrl.document, true)">
                <span>{{$ctrl.getShowMoreDuplicatesText($ctrl.document)}}</span>
                <md-button class="mw-button"
                    ng-if="$ctrl.document.dupDocsVisibleCount < $ctrl.getDuplicatesTotal($ctrl.document) && !$ctrl.document.dupDocsLoading"
                    ng-click="$ctrl.showMoreDuplicates($ctrl.document)"
                    flex="noshrink"
                    >{{'Show More' | translate}}</md-button>
            </mw-content-document-tray-footer>
            <mw-content-document-tray-loader ng-if="$ctrl.document.dupDocsLoading"></mw-content-document-tray-loader>

        </mw-content-document-duplicate-docs>

    </mw-content-document-tray>
</div>
`;

const transcludesConfig = {
    'mwContentDocumentHeader': '?mwContentDocumentHeader',

    'mwContentDocumentSourceAvatar': '?mwContentDocumentSourceAvatar',
    'mwContentDocumentMediaTypeIcon': '?mwContentDocumentMediaTypeIcon',
    'mwContentDocumentSourceName': '?mwContentDocumentSourceName',
    'mwContentDocumentAuthors': '?mwContentDocumentAuthors',
    'mwContentDocumentSourceCountry': '?mwContentDocumentSourceCountry',
    'mwContentDocumentTimestamp': '?mwContentDocumentTimestamp',
    'mwContentDocumentActions': '?mwContentDocumentActions',

    'mwContentDocumentTitle': '?mwContentDocumentTitle',
    'mwContentDocumentIngress': '?mwContentDocumentIngress',
    'mwContentDocumentMatchSentence': '?mwContentDocumentMatchSentence',
    'mwContentDocumentBody': '?mwContentDocumentBody',
    'mwContentDocumentMedia': '?mwContentDocumentMedia',
    'mwContentDocumentKeywords': '?mwContentDocumentKeywords',
    'mwContentDocumentTags': '?mwContentDocumentTags',

    'mwContentDocumentFooter': '?mwContentDocumentFooter',
    'mwContentDocumentTrayContent': '?mwContentDocumentTrayContent',
    'mwContentDocumentDuplicateDocsActions': '?mwContentDocumentDuplicateDocsActions'
};

export const mediaDocumentDisplayModeDefaults = {
    standard: {
        mediaEmphasis: 'medium',
        isDocumentClickable: false,
        isSelectable: true,
        isAvatarVisible: true,
        isAvatarPaddingOn: true,
        isTitleVisible: true,
        isIngressVisible: true,
        isMatchSentenceVisible: true,
        isKeywordsVisible: true,
        isKeywordsClickable: false,
        isMediaVisible: true,
        isMediaPlaceholderVisible: false,
        isTagsVisible: true,
        isReachVisible: true,
        isSocialEchoVisible: true,
        isSocialEngagementVisible: true,
        isDuplicatesVisible: true,
        isSentimentVisible: true
    },
    compact: {
        mediaEmphasis: 'low',
        isDocumentClickable: false,
        isSelectable: true,
        isAvatarVisible: true,
        isAvatarPaddingOn: false,
        isTitleVisible: true,
        isIngressVisible: false,
        isMatchSentenceVisible: true,
        isKeywordsVisible: false,
        isKeywordsClickable: false,
        isMediaVisible: true,
        isMediaPlaceholderVisible: false,
        isTagsVisible: false,
        isReachVisible: true,
        isSocialEchoVisible: true,
        isSocialEngagementVisible: true,
        isDuplicatesVisible: true,
        isSentimentVisible: true
    },
    mini: {
        mediaEmphasis: 'low',
        isDocumentClickable: false,
        isSelectable: false,
        isAvatarVisible: true,
        isAvatarPaddingOn: false,
        isTitleVisible: false,
        isIngressVisible: false,
        isMatchSentenceVisible: false,
        isKeywordsVisible: false,
        isKeywordsClickable: false,
        isMediaVisible: true,
        isMediaPlaceholderVisible: false,
        isTagsVisible: false,
        isReachVisible: true,
        isSocialEchoVisible: false,
        isSocialEngagementVisible: false,
        isDuplicatesVisible: false,
        isSentimentVisible: false
    },
    indy: {
        mediaEmphasis: 'high',
        isDocumentClickable: false,
        isSelectable: false,
        isAvatarVisible: true,
        isAvatarPaddingOn: false,
        isTitleVisible: false,
        isIngressVisible: false,
        isMatchSentenceVisible: false,
        isKeywordsVisible: false,
        isKeywordsClickable: false,
        isMediaVisible: true,
        isMediaPlaceholderVisible: false,
        isTagsVisible: false,
        isReachVisible: true,
        isSocialEchoVisible: false,
        isSocialEngagementVisible: false,
        isDuplicatesVisible: false,
        isSentimentVisible: true
    },
    list: {
        mediaEmphasis: 'low',
        isDocumentClickable: true,
        isSelectable: false,
        isAvatarVisible: true,
        isAvatarPaddingOn: false,
        isTitleVisible: false,
        isIngressVisible: false,
        isMatchSentenceVisible: false,
        isKeywordsVisible: false,
        isKeywordsClickable: false,
        isMediaVisible: true,
        isMediaPlaceholderVisible: false,
        isTagsVisible: false,
        isReachVisible: true,
        isSocialEchoVisible: false,
        isSocialEngagementVisible: false,
        isDuplicatesVisible: false,
        isSentimentVisible: false
    }
}

@Component({
    selector: 'mw-media-document',
    template: mediaDocumentTemplate,
    legacy: {
        transclude: transcludesConfig
    }
})
export class MediaDocumentComponent implements OnInit, OnChanges {
    @Input('<') document: MediaDocument;

    @Input('<') displayMode: string; // Supported values 'standard'(default), 'mini', 'indy'
    @Input('<') mediaEmphasis: string; // Supported values 'high', 'medium' (default), 'low'
    @Input('<') isDocumentClickable: boolean;
    @Input('<') isSelectable: boolean;
    @Input('<') isAvatarVisible: boolean;
    @Input('<') isAvatarPaddingOn: boolean;
    @Input('<') isTitleVisible: boolean;
    @Input('<') isIngressVisible: boolean;
    @Input('<') isMatchSentenceVisible: boolean;
    @Input('<') isKeywordsVisible: boolean;
    @Input('<') isKeywordsClickable: boolean;
    @Input('<') isMediaVisible: boolean;
    @Input('<') isMediaPlaceholderVisible: boolean;
    @Input('<') isTagsVisible: boolean;

    @Input('<') isReachVisible: boolean;
    @Input('<') isSocialEchoVisible: boolean;
    @Input('<') isSocialEngagementVisible: boolean;
    @Input('<') isDuplicatesVisible: boolean;
    @Input('<') isSentimentVisible: boolean;
    @Input('<') duplicatesPageSize: number;
    @Input('<') locale: string;
    @Input('<') timezone: string;
    @Input('<') isInlineSentimentEnabled: boolean = false;
    @Input('<') keywordsTruncate: number;
    @Input('<') keywordsTruncateMoreOnly: boolean;
    @Input('<') isIcmEnabled: boolean = false;
    @Input('<') activeTrayContent: string = '';

    @Output() onDocumentClick = new EventEmitter<{document: MediaDocument, event: ng.IAngularEvent}>();
    @Output() onTitleClick = new EventEmitter<MediaDocument>();
    @Output() onTimestampClick = new EventEmitter<MediaDocument>();
    @Output() onAvatarClick = new EventEmitter<MediaDocument>();
    @Output() onKeywordClick = new EventEmitter<{keyword: string}>();
    @Output() onSelectToggle = new EventEmitter<{document: MediaDocument}>();
    @Output() onSentimentUpdate = new EventEmitter<{document: MediaDocument, sentiment: string}>();
    @Output() onSentimentReset = new EventEmitter<{document: MediaDocument}>();
    @Output() onTagRemoval = new EventEmitter<{document: MediaDocument, tag: string}>();
    @Output() onDuplicatesToggle = new EventEmitter<boolean>();
    @Output() onSocialEchoToggle = new EventEmitter<boolean>();
    @Output() onReachToggle = new EventEmitter<boolean>();
    @Output() onTagClick = new EventEmitter<{tag: string}>();
    @Output() onEngagementToggle = new EventEmitter<boolean>();

    private isSocialEchoActive: boolean = false;
    private isSocialEngagementActive: boolean = false;
    private isDuplicatesActive: boolean = false;
    private isReachActive: boolean = false;
    private contentDirection = 'ltr';
    private RTL_LANGUAGES = [ // RTL languages are written from Right to Left!
        'fa', // Persian
        'he', // Hebrew
        'ar', // Arabic
        'ur', // Urdu
        'ku', // Kurdish
        'dv', // Divehi/Dhivehi/Maldivian
        'az'  // Azerbaijani
    ];

    @Input('<') isSelected: (document: any) => boolean = (document) => document.selected;

    constructor(
        @Inject('$transclude') private $transclude: ng.ITranscludeFunction,
        @Inject('$scope') private $scope,
        @Inject('$timeout') private $timeout,
        @Inject('$element') private $element,
        @Inject('$window') private $window,
        @Inject('$mdUtil') private $mdUtil,
        @Inject('$translate') private $translate
    ) {
        'ngInject';
    }

    ngOnInit() {
        this.displayMode = this.displayMode && this.displayMode !== 'compact' ? this.displayMode : 'standard';

        this.isDocumentClickable = this.isDocumentClickable !== undefined ? this.isDocumentClickable : mediaDocumentDisplayModeDefaults[this.displayMode].isDocumentClickable;
        this.mediaEmphasis = this.mediaEmphasis !== undefined ? this.mediaEmphasis : mediaDocumentDisplayModeDefaults[this.displayMode].mediaEmphasis;
        this.isAvatarVisible = this.isAvatarVisible !== undefined ? this.isAvatarVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isAvatarVisible;
        this.isAvatarPaddingOn = this.isAvatarPaddingOn !== undefined ? this.isAvatarPaddingOn : mediaDocumentDisplayModeDefaults[this.displayMode].isAvatarPaddingOn;
        this.isTitleVisible = this.isTitleVisible !== undefined ? this.isTitleVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isTitleVisible;
        this.isMatchSentenceVisible = this.isMatchSentenceVisible !== undefined ? this.isMatchSentenceVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isMatchSentenceVisible;
        this.isKeywordsVisible = this.isKeywordsVisible !== undefined ? this.isKeywordsVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isKeywordsVisible;
        this.isKeywordsClickable = this.isKeywordsClickable !== undefined ? this.isKeywordsClickable : mediaDocumentDisplayModeDefaults[this.displayMode].isKeywordsClickable;
        this.isMediaVisible = this.isMediaVisible !== undefined ? this.isMediaVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isMediaVisible;
        this.isMediaPlaceholderVisible = this.isMediaPlaceholderVisible !== undefined ? this.isMediaPlaceholderVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isMediaPlaceholderVisible;
        this.isIngressVisible = this.isIngressVisible !== undefined ? this.isIngressVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isIngressVisible;
        this.isTagsVisible = this.isTagsVisible !== undefined ? this.isTagsVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isTagsVisible;
        this.isReachVisible = this.isReachVisible !== undefined ? this.isReachVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isReachVisible;
        this.isSocialEchoVisible = this.isSocialEchoVisible !== undefined ? this.isSocialEchoVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isSocialEchoVisible;
        this.isSocialEngagementVisible = this.isSocialEngagementVisible !== undefined ? this.isSocialEngagementVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isSocialEngagementVisible;
        this.isDuplicatesVisible = this.isDuplicatesVisible !== undefined ? this.isDuplicatesVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isDuplicatesVisible;
        this.isSentimentVisible = this.isSentimentVisible !== undefined ? this.isSentimentVisible : mediaDocumentDisplayModeDefaults[this.displayMode].isSentimentVisible;

        this.duplicatesPageSize = this.duplicatesPageSize ? this.duplicatesPageSize : 10;
        this.contentDirection = (this.RTL_LANGUAGES.indexOf(this.document.language) === -1) ? 'ltr' : 'rtl';
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.activeTrayContent && changes.activeTrayContent.currentValue !== changes.activeTrayContent.previousValue) {
            if (changes.activeTrayContent.isFirstChange()) {
                this.setActiveTrayContent(changes.activeTrayContent.currentValue);
            } else {
                // don't love this but we need to force the UI to update using $timeout
                this.$timeout(() => { this.setActiveTrayContent(changes.activeTrayContent.currentValue) }, 0);
            }
        }
    }

    handleDocumentClick(document: MediaDocument, event: ng.IAngularEvent) {
        if (this.isDocumentClickable) {
            if (event && document.isHosted) {
                event.preventDefault()
            }
            this.onDocumentClick.emit({ document, event });
        }
    }

    handleTitleClick(document: MediaDocument, event: ng.IAngularEvent) {
        if (event && document.isHosted) {
            event.preventDefault()
        }
        this.onTitleClick.emit(document);
    }

    handleTimestampClick(document: MediaDocument, event: ng.IAngularEvent) {
        if (event && document.isHosted) {
            event.preventDefault()
        }
        this.onTimestampClick.emit(document);
    }

    handleAvatarClick(document: MediaDocument, event: ng.IAngularEvent) {
        if (event && document.isHosted) {
            event.preventDefault()
        }
        this.onAvatarClick.emit(document);
    }

    handleSelectToggle(document: MediaDocument, parentDocument: MediaDocument) {
        this.onSelectToggle.emit({document});
    }

    handleSentimentUpdate(document: MediaDocument, sentiment: string) {
        this.onSentimentUpdate.emit({document, sentiment});
    }

    handleSentimentReset(document: MediaDocument) {
        this.onSentimentReset.emit({document});
    }

    handleKeywordClick(keyword: string) {
        this.onKeywordClick.emit({keyword});
    }

    handleTagRemoval(document: MediaDocument, tag: string) {
        this.onTagRemoval.emit({document, tag});
    }

    handleTagClick(tag: string) {
        this.onTagClick.emit({tag});
    }

    handleSocialEchoToggle(event) {
        if (this.document.socialScores) {
            this.isDuplicatesActive = false;
            this.isSocialEngagementActive = false;
            this.isReachActive = false;
            this.isSocialEchoActive = !this.isSocialEchoActive;
            this.onSocialEchoToggle.emit(this.isSocialEchoActive);
        }
    }

    handleReach(event) {
        if (this.document.reachCount) {
            this.isDuplicatesActive = false;
            this.isSocialEchoActive = false;
            this.isReachActive = !this.isReachActive;
            this.onReachToggle.emit(this.isReachActive);
        }
    }

    handleSocialEngagementToggle(event) {
        if (this.document.engagement) {
            this.isDuplicatesActive = false;
            this.isSocialEchoActive = false;
            this.isReachActive = false;
            this.isSocialEngagementActive = !this.isSocialEngagementActive;
            this.onEngagementToggle.emit(this.isSocialEngagementActive);
        }
    }

    handleDuplicatesToggle(document: MediaDocument) {
        if (document.dupDocs && document.dupDocs.length > 0) {
            this.isSocialEchoActive = false;
            this.isSocialEngagementActive = false;
            this.isReachActive = false;
            this.isDuplicatesActive = !this.isDuplicatesActive;
            this.onDuplicatesToggle.emit(this.isDuplicatesActive);
            document.dupDocsVisibleCount = 0;

            if (this.isDuplicatesActive) {
                this.handleShowMoreDuplicates(document);
            }
        }
    }

    handleShowMoreDuplicates(document: MediaDocument) {
        if (!document.dupDocsCount || document.dupDocs.length === document.dupDocsCount) {
            this.showMoreDuplicates(document);
        }
    }

    showMoreDuplicates(document: MediaDocument) {
        if (!document.dupDocsLoading) {
            document.dupDocsLoading = true;

            this.$timeout(() => {
                if ( (document.dupDocsVisibleCount + this.duplicatesPageSize) > document.dupDocs.length) {
                    document.dupDocsVisibleCount = document.dupDocs.length;
                } else {
                    document.dupDocsVisibleCount = document.dupDocsVisibleCount + this.duplicatesPageSize;
                }
                document.dupDocsLoading = false;
            }, 500);
        }
    }

    getShowMoreDuplicatesText(document: MediaDocument) {
        let total = this.getDuplicatesTotal(document);
        return this.$translate.instant('mw-content-document__duplicated-docs--displayed', {count: document.dupDocsVisibleCount, total});
    }

    getDuplicatesTotal(document: MediaDocument) {
        return document.dupDocsCount || document.dupDocs.length || 0;
    }

    setActiveTrayContent(activeTrayContent: string) {
        const validActiveTrayContent = ['duplicates', 'socialEcho', 'socialEngagement', 'reach'];

        if (activeTrayContent && validActiveTrayContent.indexOf(activeTrayContent) > -1) {
            const activeTrayToggleFnName = 'handle' + activeTrayContent[0].toUpperCase() + activeTrayContent.substr(1) + 'Toggle';
            if (activeTrayContent === 'duplicates') {
                this[activeTrayToggleFnName](this.document);
            } else {
                this[activeTrayToggleFnName]();
            }
        } else {
            this.isSocialEchoActive = false;
            this.isReachActive = false;
            this.isSocialEngagementActive = false;
            this.isDuplicatesActive = false;
        }
    }
}


/**
 * @deprecated use mw-media-document instead
 *
 */
@Component({
    selector: 'mw-content-document',
    template: mediaDocumentTemplate,
    legacy: {
        transclude: transcludesConfig
    }
})
export class ContentDocumentComponent extends MediaDocumentComponent implements OnInit {}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/media-document/media-document.component.ts